/** @jsxImportSource @emotion/react */

import { FC } from 'react'
import { useField, useForm } from 'react-final-form'
import { NumberFormatValues, NumericFormat } from 'react-number-format'
import { FieldValidator } from 'final-form'

import OutlinedInput, { OutlinedInputProps } from '@mui/material/OutlinedInput'
import FormHelperText from '@mui/material/FormHelperText'
import FormControlLabel from '@mui/material/FormControlLabel'
import * as classes from './styles'

export type InputProps = Omit<OutlinedInputProps, 'value'> & {
  value: string
  formHelperText?: string
}

export function Input({ label, error, formHelperText, fullWidth, className, ...props }: InputProps) {
  const controlInput = <OutlinedInput error={error} {...props} />

  return (
    <div className={className} css={classes.wrapper(fullWidth)}>
      {label ? (
        <FormControlLabel control={controlInput} label={label} labelPlacement="top" css={classes.labelWrapper} />
      ) : (
        controlInput
      )}
      {!!formHelperText && <FormHelperText error={error}>{formHelperText}</FormHelperText>}
    </div>
  )
}

type Props = Omit<InputProps, 'value' | 'onChange'> & {
  name: string
  validate?: FieldValidator<string>
  prefix?: string
  suffix?: string
  allowNegative?: boolean
  decimalScale?: number
}

export const PriceInput: FC<Props> = ({
  name,
  validate,
  prefix,
  suffix,
  allowNegative = false,
  decimalScale = 2,
  ...props
}) => {
  const form = useForm()
  const { input, meta } = useField<string>(name, { validate })

  const handleInputChange = (values: NumberFormatValues) => {
    form.change(name, values.floatValue)
  }

  const errorMessage = meta.error || meta.submitError || props.error

  return (
    <NumericFormat
      {...props}
      value={input.value}
      customInput={Input}
      onValueChange={handleInputChange}
      onBlur={input.onBlur}
      onFocus={input.onFocus}
      allowNegative={allowNegative}
      decimalScale={decimalScale}
      prefix={prefix}
      suffix={suffix}
      defaultValue={String(props.defaultValue)}
      type="text"
      error={Boolean(meta.touched && errorMessage)}
      formHelperText={(meta.touched && errorMessage) || undefined}
    />
  )
}
